import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { api } from '@/lib/api'
import { BarChart, type BarDatum } from '@/components/BarChart'
import { ErrorState, Skeleton } from '@/components/States'
import { RefreshButton } from '@/components/RefreshButton'

// A24.05 — SMS provayderlar (Infobip). Backend: GET /admin/sms/stats (`handlers/admin.go` SmsStats).
// Infobip real reporting: balans + yuborilgan SMS statistikasi (davlat/status/oxirgi). OTP kodlari
// backendда maskalangan. Admin-gated operatsion ekran (kalit server ENV'да, repoда emas).
interface SmsStatsData {
  provider: string
  mode: string
  configured: boolean
  balance?: number
  currency?: string
  total_sent?: number
  by_country: { country: string; count: number }[]
  by_status: { status: string; count: number }[]
  recent: { to: string; text: string; status: string; group: string; sent_at: string }[]
  error?: string
  balance_error?: string
  logs_error?: string
}
function fetchSmsStats(signal?: AbortSignal): Promise<SmsStatsData> {
  return api.get<SmsStatsData>('/admin/sms/stats', signal ? { signal } : undefined)
}

function KPI({ label, value, loading, accent }: { label: string; value?: string; loading: boolean; accent?: boolean }) {
  return (
    <div className="rounded-lg border border-border-default bg-surface-2 p-3">
      <div className="text-xs text-tertiary">{label}</div>
      {loading ? (
        <Skeleton className="mt-1.5 h-8 w-20" />
      ) : (
        <div className={`mt-0.5 font-mono text-2xl tabular ${accent ? 'text-accent' : 'text-primary'}`}>{value ?? '—'}</div>
      )}
    </div>
  )
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="max-w-3xl rounded-lg border border-border-default bg-surface-2 p-3">
      <div className="mb-2 text-sm font-medium text-secondary">{title}</div>
      {children}
    </div>
  )
}

interface SmsLogItem {
  id: number
  phone: string
  message: string
  provider: string
  purpose?: string | null
  status: string
  error?: string | null
  created_at: string
}

/** Bizning SMS log: HAR bir yuborish urinishi (raqam / matn / vaqt / natija).
 *  Backend GET /admin/sms/log — Infobip hisobotidан farqli, bu bizning tomondan
 *  yuborilgan har bir SMS (muvaffaqiyatли + xato) + raqam bo'yicha qidiruv. */
function SmsLogPanel() {
  const [items, setItems] = useState<SmsLogItem[]>([])
  const [loading, setLoading] = useState(true)
  const [phone, setPhone] = useState('')
  const [q, setQ] = useState('')
  const [reload, setReload] = useState(0)
  useEffect(() => {
    const ac = new AbortController()
    setLoading(true)
    const qs = q ? `?phone=${encodeURIComponent(q)}&limit=200` : '?limit=200'
    api
      .get<{ items: SmsLogItem[] }>(`/admin/sms/log${qs}`, { signal: ac.signal })
      .then((d) => setItems(d.items ?? []))
      .catch(() => {})
      .finally(() => setLoading(false))
    return () => ac.abort()
  }, [reload, q])
  return (
    <Panel title="SMS log — har bir yuborish (raqam / matn / vaqt / natija)">
      <div className="mb-2 flex gap-2">
        <input
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && setQ(phone.trim())}
          placeholder="Raqam bo'yicha qidirish…"
          className="w-52 rounded-md border border-border-default bg-surface-2 px-2 py-1 text-sm text-primary outline-none"
        />
        <button
          onClick={() => setQ(phone.trim())}
          className="rounded-md border border-border-default px-3 py-1 text-sm text-secondary"
        >
          Qidirish
        </button>
        <button
          onClick={() => setReload((n) => n + 1)}
          className="rounded-md border border-border-default px-3 py-1 text-sm text-secondary"
        >
          ↻
        </button>
      </div>
      {loading ? (
        <Skeleton className="h-40 w-full" />
      ) : !items.length ? (
        <div className="py-6 text-center text-sm text-tertiary">Ma'lumot yo'q</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[620px] border-collapse text-sm">
            <thead>
              <tr className="text-left text-xs text-tertiary">
                <th className="py-1 pr-2 font-medium">Raqam</th>
                <th className="py-1 pr-2 font-medium">Xabar</th>
                <th className="py-1 pr-2 font-medium">Provayder</th>
                <th className="py-1 pr-2 font-medium">Natija</th>
                <th className="py-1 font-medium">Vaqt</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-t border-border-subtle">
                  <td className="py-1.5 pr-2 font-mono text-xs text-primary">{r.phone}</td>
                  <td className="py-1.5 pr-2 text-secondary">{r.message}</td>
                  <td className="py-1.5 pr-2 text-tertiary">{r.provider}</td>
                  <td
                    className="py-1.5 pr-2 text-xs"
                    style={{ color: r.status === 'failed' ? '#e5484d' : '#3ba55d' }}
                  >
                    {r.status}
                    {r.error ? ` · ${r.error}` : ''}
                  </td>
                  <td className="py-1.5 font-mono text-xs text-tertiary">
                    {r.created_at?.slice(0, 19).replace('T', ' ')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  )
}

/** A24.05 — SMS provayderlar: Infobip holati, balans va yuborilgan SMS statistikasi. */
export function SmsProviders() {
  const t = useT()
  const [data, setData] = useState<SmsStatsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)
  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchSmsStats(ac.signal)
      .then((d) => {
        if (alive) setData(d)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [reload])
  const countryBars = useMemo<BarDatum[]>(
    () => (data?.by_country ?? []).map((c) => ({ label: c.country, value: c.count })),
    [data],
  )
  const statusBars = useMemo<BarDatum[]>(
    () => (data?.by_status ?? []).map((s) => ({ label: s.status, value: s.count })),
    [data],
  )
  if (error) return <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
  const balanceStr =
    data?.balance !== undefined ? `${data.balance.toFixed(2)} ${data.currency ?? ''}`.trim() : undefined
  return (
    <>
      <PageHeader
        title={t.sms.title}
        screenId="A24.05"
        description={t.sms.note}
        actions={<RefreshButton onClick={() => setReload((n) => n + 1)} />}
      />
      <div className="min-h-0 flex-1 space-y-3 overflow-auto p-3">
        <div className="grid max-w-3xl grid-cols-2 gap-2 md:grid-cols-4">
          <KPI label={t.sms.balance} value={balanceStr} loading={loading} accent />
          <KPI label={t.sms.totalSent} value={data?.total_sent?.toLocaleString()} loading={loading} />
          <KPI
            label={t.sms.mode}
            value={data ? (data.configured ? (data.mode || 'infobip') : t.sms.notConfigured) : undefined}
            loading={loading}
          />
          <KPI label="Provider" value={data?.provider} loading={loading} />
        </div>
        <Panel title={t.sms.byCountry}>
          {loading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <BarChart data={countryBars} emptyLabel={t.dash.noData} tableLabel={t.dash.valuesTable} />
          )}
        </Panel>
        <Panel title={t.sms.byStatus}>
          {loading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <BarChart data={statusBars} emptyLabel={t.dash.noData} tableLabel={t.dash.valuesTable} />
          )}
        </Panel>
        <Panel title={t.sms.recent}>
          {loading ? (
            <Skeleton className="h-40 w-full" />
          ) : !data?.recent.length ? (
            <div className="py-6 text-center text-sm text-tertiary">{t.dash.noData}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[520px] border-collapse text-sm">
                <thead>
                  <tr className="text-left text-xs text-tertiary">
                    <th className="py-1 pr-2 font-medium">{t.sms.colNumber}</th>
                    <th className="py-1 pr-2 font-medium">{t.sms.colMessage}</th>
                    <th className="py-1 pr-2 font-medium">{t.sms.colStatus}</th>
                    <th className="py-1 font-medium">{t.sms.colTime}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent.map((r, i) => (
                    <tr key={i} className="border-t border-border-subtle">
                      <td className="py-1.5 pr-2 font-mono text-xs text-primary">{r.to}</td>
                      <td className="py-1.5 pr-2 text-secondary">{r.text}</td>
                      <td className="py-1.5 pr-2 text-secondary">{r.status}</td>
                      <td className="py-1.5 font-mono text-xs text-tertiary">{r.sent_at?.slice(0, 19).replace('T', ' ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
        <SmsLogPanel />
      </div>
    </>
  )
}
